export type DigitalAssetMode = "list" | "add" | "edit" | "view";

export interface DigitalAsset {
  id: number;
  name: string;
  officeId: number;
  officeName: string;
  officeCode: string;
  storeNumber?: string | number | null;
  agency?: string | null;
  officeStatus?: string | null;
  countryCode?: string | null;
  online_Directories_Brand_Name__C?: string | null;
  serviceName?: string | null;
  digital_Tracking_Number__C?: string | null;
  shortDescription?: string | null;
  page_Title__C?: string | null;
  page_Title_Spa__C?: string | null;
  website__C?: string | null;
  website_Local_Profile_Url__C?: string | null;
  website_Local_Profile_Url_Spa__C?: string | null;
  page_Description__C?: string | null;
  page_Description_Spa__C?: string | null;
  google_Business_Url__C?: string | null;
  google_Description__C?: string | null;
  yelp_Profile_Url__C?: string | null;
  yelp_Description_Specialties__C?: string | null;
  title_Tag__C?: string | null;
  title_Tag_Spa__C?: string | null;
  meta_Description__C?: string | null;
  meta_Description_Spa__C?: string | null;
  facebook_Username_Page_Url__C?: string | null;
  directory_General_Description__C?: string | null;
  directory_Company_Story__C?: string | null;
  facebook_Company_Overview__C?: string | null;
  bing?: string | null;
  bingDescription?: string | null;
  appleURL?: string | null;
  appleDescription?: string | null;
  appleCategoryId?: number | null;
  appleCategoryName?: string | null;
  appleAppNameId?: number | null;
  appleAppName?: string | null;
  appleQuickLink?: string | null;
  twitterUrl?: string | null;
  linkedInUrl?: string | null;
  youtubeUrl?: string | null;
  instagramUrl?: string | null;
  createdBy?: string | null;
  createdOn?: string | null;
  updatedBy?: string | null;
  updatedOn?: string | null;
  isActive: boolean;
}

export interface DigitalAssetFormValues extends Omit<
  DigitalAsset,
  "id" | "isActive" | "serviceName"
> {
  id?: number;
  serviceNames: string[];
  isActive?: boolean;
}

export interface OfficeOption {
  officeId: number;
  officeName: string;
  officeCode: string;
  storeNumber?: string | number | null;
  agency?: string | null;
  brandMarketingName?: string | null;
  officeStatus?: string | null;
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface DigitalAssetPermissions {
  canAdd: boolean;
  canEdit: boolean;
  canDeactivate: boolean;
  canDownload: boolean;
  canViewOffice: boolean;
  visibleFields?: ReadonlySet<string>;
}

export const EMPTY_DIGITAL_ASSET_PERMISSIONS: DigitalAssetPermissions = {
  canAdd: false,
  canEdit: false,
  canDeactivate: false,
  canDownload: false,
  canViewOffice: false,
};

export const toFormValues = (
  asset?: DigitalAsset | null,
): DigitalAssetFormValues => ({
  ...(asset ?? {
    name: "",
    officeId: 0,
    officeName: "",
    officeCode: "",
  }),
  serviceNames: asset?.serviceName
    ? asset.serviceName
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
    : [],
});

export const toApiPayload = (
  values: DigitalAssetFormValues,
): Record<string, unknown> => {
  const { serviceNames, ...fields } = values;
  return { ...fields, serviceName: serviceNames.join(",") };
};

export interface DigitalAssetDownloadRow {
  digitalAssetId: number;
  digitalAssetName: string;

  officeId?: number | null;
  officeCode?: string | null;
  officeName?: string | null;
  storeNumber?: string | number | null;

  countryCode?: string | null;
  officeStatus?: string | null;
  agency?: string | null;

  digitalTrackingNumber?: string | null;

  website?: string | null;

  directoryGeneralDescription?: string | null;

  directoryCompanyStory?: string | null;

  directory_Company_Story__C?: string | null;

  facebookCompanyOverview?: string | null;

  facebookUsernamePageUrl?: string | null;

  googleBusinessUrl?: string | null;

  googleDescription?: string | null;

  metaDescription?: string | null;

  metaDescriptionSpa?: string | null;

  onlineDirectoriesBrandaName?: string | null;

  pageDescription?: string | null;

  pageDescriptionSpa?: string | null;

  pageTitle?: string | null;
  pageTitleSpa?: string | null;

  titleTag?: string | null;
  titleTagSpa?: string | null;

  websiteLocalProfileUrl?: string | null;

  websiteLocalProfileUrlSpa?: string | null;

  yelpDescriptionSpecialties?: string | null;

  yelpProfileUrl?: string | null;

  twitterUrl?: string | null;
  linkedInUrl?: string | null;
  youtubeUrl?: string | null;
  instagramUrl?: string | null;

  appleUrl?: string | null;
  appleDescription?: string | null;
  appleCategoryName?: string | null;
  appleAppName?: string | null;
  appleQuickLink?: string | null;

  bing?: string | null;
  bingDescription?: string | null;

  shortDescription?: string | null;

  createdBy?: string | null;
  createdOn?: string | null;

  updatedBy?: string | null;
  updatedOn?: string | null;

  serviceName?: string | null;

  lastSyncDate?: string | null;
  isSync?: string | null;

  lastModifiedDate?: string | null;

  isActive?: boolean | null;
}

export interface DigitalAssetAppleAppName {
  appleAppNameId: number;
  appleAppName: string;
}

export interface DigitalAssetAppleCategory {
  digitalAssetAppleCategoryId: number;
  digitalAssetAppleCategoryName: string;
}
